import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import type { OAuthConfiguration, OAuthTokens, StorageLike } from './oauth.ts';
import {
  clearOAuthTokens,
  getAccessToken,
  getRefreshToken,
  saveOAuthTokens
} from './oauth.ts';

type RetryConfig = AxiosRequestConfig & {
  _opstrackOAuthRetry?: boolean;
};

export type ApiAuthenticationOptions = {
  storage: StorageLike;
  getConfiguration: () => OAuthConfiguration;
  refreshTokens: (
    config: OAuthConfiguration,
    refreshToken: string
  ) => Promise<OAuthTokens>;
  onAuthenticationFailure: () => void;
};

export type ApiAuthenticationController = {
  disable: () => void;
  enable: () => void;
};

function isOAuthRequest(config: AxiosRequestConfig) {
  const url = config.url ?? '';

  try {
    return new URL(url, config.baseURL).pathname.startsWith('/o/');
  } catch {
    return url.startsWith('/o/');
  }
}

function setAuthorization(
  config: AxiosRequestConfig,
  accessToken: string | null
) {
  config.headers ??= {};

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    delete config.headers.Authorization;
  }
}

export function installApiAuthentication(
  api: AxiosInstance,
  options: ApiAuthenticationOptions
): ApiAuthenticationController {
  let enabled = true;
  let refreshInFlight: Promise<OAuthTokens> | undefined;
  let refreshGeneration = 0;
  let failureHandled = false;

  function failAuthentication() {
    if (failureHandled) return;

    failureHandled = true;
    clearOAuthTokens(options.storage);
    options.onAuthenticationFailure();
  }

  async function refreshAccessToken() {
    if (refreshInFlight) {
      return refreshInFlight;
    }

    const refreshToken = getRefreshToken(options.storage);
    if (!enabled || !refreshToken) {
      throw new Error('OAuth refresh is unavailable');
    }

    const generation = refreshGeneration;
    refreshInFlight = options
      .refreshTokens(options.getConfiguration(), refreshToken)
      .then((tokens) => {
        if (!enabled || generation !== refreshGeneration) {
          throw new Error('OAuth refresh was cancelled');
        }

        saveOAuthTokens(options.storage, tokens);
        return tokens;
      })
      .finally(() => {
        refreshInFlight = undefined;
      });

    return refreshInFlight;
  }

  api.interceptors.request.use((config) => {
    const accessToken = enabled ? getAccessToken(options.storage) : null;
    if (!accessToken) {
      setAuthorization(config, null);
      return config;
    }
    const host = options.getConfiguration().apiHost;
    const target = new URL(config.url ?? '', config.baseURL ?? host);
    if (target.origin !== new URL(host).origin || isOAuthRequest(config)) {
      setAuthorization(config, null);
      return config;
    }

    setAuthorization(config, enabled ? getAccessToken(options.storage) : null);
    // The same Axios config survives OAuth retry. Never replace an existing key.
    if (config.method?.toLowerCase() === 'post' &&
        /^\/api\/(?:v1\/)?stock\/(?:$|(?:add|remove|count|transfer)\/$|tool\/(?:available\/|assigned\/)?$|\d+\/serialize\/$)/.test(target.pathname)) {
      config.headers['Idempotency-Key'] ??= crypto.randomUUID();
    }
    return config;
  });

  api.interceptors.response.use(undefined, async (error) => {
    const config = error.config as RetryConfig | undefined;

    if (
      error.response?.status !== 401 ||
      !config ||
      config._opstrackOAuthRetry ||
      isOAuthRequest(config) ||
      !enabled ||
      !getRefreshToken(options.storage)
    ) {
      return Promise.reject(error);
    }

    config._opstrackOAuthRetry = true;

    try {
      const tokens = await refreshAccessToken();
      setAuthorization(config, tokens.access_token);
      return api.request(config);
    } catch {
      failAuthentication();
      return Promise.reject(error);
    }
  });

  return {
    disable: () => {
      enabled = false;
      refreshGeneration += 1;
      clearOAuthTokens(options.storage);
    },
    enable: () => {
      enabled = true;
      failureHandled = false;
    }
  };
}
